import { useState } from "react";
import { toast } from "sonner";
import {
    CheckCircle, DownloadSimple, FileXls, ShieldCheck, UploadSimple, Warning,
} from "@phosphor-icons/react";
import { adminToken, endpoints } from "@/lib/api";

export default function BatchContribution() {
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState([]);
    const isAdmin = !!adminToken.get();

    const upload = async () => {
        if (!file) return;
        setBusy(true);
        setErrors([]);
        setResult(null);
        try {
            const response = await endpoints.wizardBatchUpload(file, adminToken.get());
            setResult(response);
            setFile(null);
            toast.success(`${response.imported} entries uploaded for review`);
        } catch (error) {
            const detail = error?.response?.data?.detail;
            const rowErrors = detail?.errors || [];
            setErrors(rowErrors);
            toast.error(detail?.message || detail || "Batch upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="max-w-4xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <section className="border border-neutral-200 bg-white rounded-sm p-5">
                    <div className="w-10 h-10 rounded-sm bg-[#F5F3FF] text-[#6B46C1] flex items-center justify-center mb-4">
                        <DownloadSimple size={21} weight="duotone" />
                    </div>
                    <div className="label-caps mb-2">Step 1</div>
                    <h2 className="font-serif text-2xl">Download the Excel template</h2>
                    <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                        The workbook follows Geography → Primary category → Subcategory → Item → Process → Parameter. It includes
                        the fixed category lists and ten blank entry rows. Each row is reviewed once, including its new directory items.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <a
                            href="/SustainDex_Contribution_Template.xlsx"
                            download
                            className="btn-secondary inline-flex text-sm px-4 py-2.5"
                        >
                            <DownloadSimple size={15} /> Blank template
                        </a>

                    </div>
                </section>

                <section className="border border-neutral-200 bg-white rounded-sm p-5">
                    <div className="w-10 h-10 rounded-sm bg-green-50 text-green-700 flex items-center justify-center mb-4">
                        <UploadSimple size={21} weight="duotone" />
                    </div>
                    <div className="label-caps mb-2">Step 2</div>
                    <h2 className="font-serif text-2xl">Upload completed entries</h2>
                    <p className="text-xs text-neutral-500 mt-2 leading-relaxed">
                        {isAdmin
                            ? "Admin mode is active. There is no row-count limit for this upload."
                            : "Public uploads accept up to 10 populated rows per workbook."}
                    </p>
                    <label className="mt-4 min-h-28 border border-dashed border-neutral-300 hover:border-[#6B46C1] bg-neutral-50 rounded-sm flex flex-col items-center justify-center text-center cursor-pointer px-4 transition-colors">
                        <FileXls size={24} className="text-[#6B46C1] mb-2" />
                        <span className="text-sm font-medium">{file?.name || "Choose an .xlsx workbook"}</span>
                        <span className="text-[10px] text-neutral-500 mt-1">Maximum file size: 15 MB</span>
                        <input
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="sr-only"
                            onChange={(event) => {
                                setFile(event.target.files?.[0] || null);
                                setErrors([]);
                                setResult(null);
                            }}
                        />
                    </label>
                    <button
                        type="button"
                        onClick={upload}
                        disabled={!file || busy}
                        className="btn-primary mt-3 w-full justify-center py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? "Validating and uploading…" : "Upload entries for review"}
                    </button>
                </section>
            </div>

            <div className={`mt-4 border rounded-sm p-4 flex items-start gap-3 ${isAdmin ? "border-blue-200 bg-blue-50" : "border-yellow-200 bg-yellow-50"}`}>
                <ShieldCheck size={17} weight="fill" className={isAdmin ? "text-blue-700" : "text-yellow-800"} />
                <div className="text-xs leading-relaxed">
                    <b>{isAdmin ? "Administrator batch mode" : "Public batch mode"}</b>
                    <div className="text-neutral-600 mt-0.5">
                        All datasets and newly created taxonomy entries enter the review queue before appearing publicly.
                    </div>
                </div>
            </div>

            {result && (
                <div className="mt-4 border border-green-200 bg-green-50 rounded-sm p-5 flex items-start gap-3">
                    <CheckCircle size={22} weight="fill" className="text-green-700 shrink-0" />
                    <div>
                        <div className="font-semibold">Upload completed</div>
                        <p className="text-sm text-green-900 mt-1">
                            {result.imported} {result.imported === 1 ? "entry" : "entries"} added to the admin review queue.
                        </p>
                    </div>
                </div>
            )}

            {errors.length > 0 && (
                <div className="mt-4 border border-red-200 bg-red-50 rounded-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-red-200 flex items-center gap-2 font-semibold text-sm text-red-900">
                        <Warning size={16} weight="fill" /> Fix these rows and upload again
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-red-100">
                        {errors.map((error, index) => (
                            <div key={`${error.row}-${index}`} className="px-4 py-2.5 text-xs flex gap-3">
                                <span className="font-mono font-semibold text-red-800">Row {error.row}</span>
                                <span className="text-red-900">{error.message}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
